import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const PADS = [
  { id: 0, color: "#FF003C", active: "#ff4d77" },
  { id: 1, color: "#00F0FF", active: "#7df4ff" },
  { id: 2, color: "#39FF14", active: "#a5ff8f" },
  { id: 3, color: "#EFFF04", active: "#f7ff7a" },
];

export function SimonGame({ onScore }) {
  const [seq, setSeq] = useState([]);
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [showing, setShowing] = useState(false);
  const [round, setRound] = useState(0);
  const [over, setOver] = useState(false);
  const inputIdx = useRef(0);
  const reported = useRef(false);

  const flash = (id, ms = 400) =>
    new Promise((res) => { setActive(id); setTimeout(() => { setActive(null); setTimeout(res, 120); }, ms); });

  const playSeq = async (s) => {
    setShowing(true);
    await new Promise((r) => setTimeout(r, 500));
    for (const id of s) await flash(id);
    setShowing(false);
  };

  const nextRound = async (cur) => {
    const ns = [...cur, Math.floor(Math.random() * 4)];
    setSeq(ns);
    setRound(ns.length);
    inputIdx.current = 0;
    await playSeq(ns);
  };

  const start = () => {
    setOver(false); setSeq([]); setRound(0); reported.current = false;
    setPlaying(true);
    nextRound([]);
  };

  const press = async (id) => {
    if (!playing || showing) return;
    await flash(id, 200);
    if (seq[inputIdx.current] !== id) {
      setOver(true); setPlaying(false);
      return;
    }
    inputIdx.current += 1;
    if (inputIdx.current === seq.length) {
      await new Promise((r) => setTimeout(r, 400));
      nextRound(seq);
    }
  };

  useEffect(() => {
    if (over && !reported.current) {
      reported.current = true;
      onScore?.((round - 1) * 100);
    }
  }, [over, round, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="simon-game">
      <div className="flex items-center gap-4 mb-5 font-mono">
        <span className="text-neon-cyan text-lg">ROUND: {round}</span>
        {!playing && (
          <Button onClick={start} data-testid="simon-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
            <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Retry" : "Start"}
          </Button>
        )}
      </div>

      <div className="relative grid grid-cols-2 gap-3 p-4 bg-zinc-900 rounded-2xl border-2 border-zinc-700"
        style={{ width: "min(86vw, 320px)" }}>
        {PADS.map((p) => (
          <button key={p.id} data-testid={`simon-pad-${p.id}`} onClick={() => press(p.id)}
            className="aspect-square rounded-xl transition-all duration-100"
            style={{
              background: active === p.id ? p.active : p.color,
              opacity: active === p.id ? 1 : 0.55,
              boxShadow: active === p.id ? `0 0 30px ${p.color}` : "none",
              transform: active === p.id ? "scale(0.96)" : "scale(1)",
              cursor: playing && !showing ? "pointer" : "default",
            }} />
        ))}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 rounded-full bg-zinc-950 border-2 border-zinc-700 flex items-center justify-center font-display font-black text-neon-cyan">
            {showing ? "👀" : round}
          </div>
        </div>
      </div>

      {over && (
        <p className="mt-4 font-display font-black text-xl text-neon-pink uppercase" data-testid="simon-gameover">
          Wrong! You reached round {round}
        </p>
      )}
      <p className="text-zinc-500 text-xs font-mono mt-3">Watch the pattern, then repeat it</p>
    </div>
  );
}
